import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

export type RowActionItem = {
  label: string
  onSelect: () => void
  danger?: boolean
  disabled?: boolean
  children?: RowActionItem[]
}

type RowActionsProps = {
  items: RowActionItem[]
  stopRowClick?: boolean
}

const MENU_W = 192
const SUB_W = 176
const PAD = 8

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n))
}

/**
 * Kebab actions for tables/cards.
 * Menu is portaled to document.body with position:fixed so parents with
 * overflow-x-auto (carte-admin tables) never clip it.
 */
export function RowActions({ items, stopRowClick = true }: RowActionsProps) {
  const [open, setOpen] = useState(false)
  const [sub, setSub] = useState<string | null>(null)
  const [style, setStyle] = useState<CSSProperties | null>(null)
  const [subStyle, setSubStyle] = useState<CSSProperties | null>(null)

  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const subBtnRef = useRef<HTMLButtonElement>(null)
  const subMenuRef = useRef<HTMLDivElement>(null)
  const menuId = useId()

  const close = () => {
    setOpen(false)
    setSub(null)
    setStyle(null)
    setSubStyle(null)
  }

  useLayoutEffect(() => {
    if (!open) return
    const el = triggerRef.current
    if (!el) return

    const update = () => {
      const a = el.getBoundingClientRect()
      const mh = menuRef.current?.offsetHeight ?? Math.max(items.length * 40 + 10, 52)
      const vw = window.innerWidth
      const vh = window.innerHeight
      const spaceBelow = vh - a.bottom - PAD
      const spaceAbove = a.top - PAD
      // Prefer below; flip above when the menu would leave the viewport
      const openUp = spaceBelow < mh && spaceAbove >= Math.min(mh, spaceBelow + 1)
      let top = openUp ? a.top - mh - 4 : a.bottom + 4
      if (!openUp && top + mh > vh - PAD) {
        top = Math.max(PAD, a.top - mh - 4)
      }
      top = clamp(top, PAD, Math.max(PAD, vh - mh - PAD))
      const left = clamp(a.right - MENU_W, PAD, vw - MENU_W - PAD)
      setStyle({
        position: 'fixed',
        top,
        left,
        minWidth: MENU_W,
        zIndex: 1000,
      })
    }

    update()
    // second pass once menu is measured in the DOM
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(update)
    })
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, items.length, sub])

  useLayoutEffect(() => {
    if (!open || !sub) {
      setSubStyle(null)
      return
    }
    const btn = subBtnRef.current
    if (!btn) return
    const update = () => {
      const r = btn.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight
      const sh = subMenuRef.current?.offsetHeight ?? 220
      const openLeft = r.left > SUB_W + PAD
      const left = openLeft
        ? clamp(r.left - SUB_W - 4, PAD, vw - SUB_W - PAD)
        : clamp(r.right + 4, PAD, vw - SUB_W - PAD)
      const top = clamp(r.top, PAD, vh - sh - PAD)
      setSubStyle({
        position: 'fixed',
        top,
        left,
        minWidth: SUB_W,
        zIndex: 1001,
        maxHeight: `min(20rem, calc(100vh - ${PAD * 2}px))`,
        overflowY: 'auto',
      })
    }
    update()
    const raf = requestAnimationFrame(update)
    return () => cancelAnimationFrame(raf)
  }, [open, sub])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (menuRef.current?.contains(t)) return
      if (subMenuRef.current?.contains(t)) return
      close()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function leaf(item: RowActionItem): ReactNode {
    return (
      <button
        key={item.label}
        type="button"
        role="menuitem"
        disabled={item.disabled}
        className={`block w-full px-3 py-2.5 text-left text-sm hover:bg-gris-fond disabled:opacity-40 ${
          item.danger ? 'text-rouge-texte' : 'text-encre-admin'
        }`}
        onClick={(e) => {
          e.stopPropagation()
          close()
          item.onSelect()
        }}
      >
        {item.label}
      </button>
    )
  }

  const portal =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div data-row-actions-portal="">
            {style ? (
              <div
                ref={menuRef}
                id={menuId}
                role="menu"
                style={style}
                className="rounded-champ border border-bordure-admin bg-white py-1 shadow-mega"
                onClick={(e) => e.stopPropagation()}
              >
                {items.map((item) => {
                  if (!item.children?.length) return leaf(item)
                  const isOpen = sub === item.label
                  return (
                    <button
                      key={item.label}
                      ref={isOpen || sub === null ? subBtnRef : undefined}
                      type="button"
                      role="menuitem"
                      disabled={item.disabled}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm text-encre-admin hover:bg-gris-fond disabled:opacity-40"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSub(isOpen ? null : item.label)
                      }}
                      onMouseEnter={() => setSub(item.label)}
                    >
                      <span>{item.label}</span>
                      <span aria-hidden className="text-second-admin">
                        ›
                      </span>
                    </button>
                  )
                })}
              </div>
            ) : null}
            {sub && subStyle
              ? (() => {
                  const children = items.find((i) => i.label === sub)?.children
                  if (!children?.length) return null
                  return (
                    <div
                      ref={subMenuRef}
                      role="menu"
                      style={subStyle}
                      className="rounded-champ border border-bordure-admin bg-white py-1 shadow-mega"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {children.map((c) => leaf(c))}
                    </div>
                  )
                })()
              : null}
          </div>,
          document.body,
        )
      : null

  return (
    <div
      className="inline-flex"
      onClick={stopRowClick ? (e) => e.stopPropagation() : undefined}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label="Actions"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        className="grid size-8 place-items-center rounded-champ text-encre-admin hover:bg-gris-fond"
        onClick={(e) => {
          e.stopPropagation()
          if (open) close()
          else {
            setSub(null)
            setOpen(true)
          }
        }}
      >
        <span aria-hidden className="text-lg leading-none tracking-tighter">
          ⋯
        </span>
      </button>
      {portal}
    </div>
  )
}
